// The candidate "Report" action's dialogue — `extension-alert-candidates-report-enrichment-modal.tpl.html`
// wrapping `<extension-alert-candidates-report-enrichment>` — and the "Thanks for reporting!" dialogue
// that follows it (`…-report-enrichment-success-modal.tpl.html`, shown by
// `ShortlistReportEnrichmentService.showReportEnrichmentSentDialogue`).
//
// Opened upstream by the snippet's `onReportEnrichment` through `modalService.show(…, {
// useUnifiedTemplate: true, windowClass: "dialog-unified shortlist-dialogue " + (on the
// CandidateProfile state ? "shortlist-export-enrichment-modal-slim" : "shortlist-export-enrichment-modal")
// })`. The window class is where the dialogue's size lives (`_extension-alert-candidate-snippet.scss`:
// 700×555 or 360×600), and `.linkedin-url-container`'s flex row only under the wide one — so the
// slim dialogue stacks the URL field under its checkbox, which is upstream's layout, not a defect.
//
// ⚠️ SEND IS FIRE-AND-FORGET UPSTREAM. `sendEnrichmentReport()` calls the API without waiting and
// calls `onReportSent()` straight after, so the "Thanks" dialogue appears whether or not the save
// succeeded; a failure surfaces only through the generic request-failure alert. Kept.
import { createElement, useEffect, useRef, useState, type ReactElement } from 'react'
import { EnrichmentReportReason, type EnrichmentReportPayload } from '@woo-ext/api'
import { WooCheckbox } from '../common/WooCheckbox.tsx'
import { UnifiedModal } from '../modal/UnifiedModal.tsx'
import {
  INITIAL_REPORT_ENRICHMENT_FORM,
  REPORT_ENRICHMENT_REASONS,
  REPORT_ENRICHMENT_SENT_AUTO_CLOSE_MS,
  buildEnrichmentReportPayload,
  changeLinkedinUrl,
  changeReportNotes,
  toggleReportReason,
} from './reportEnrichment.ts'

export interface ReportEnrichmentModalProps {
  candidateId: number
  /** `{{::(!!ctrl.candidateName ? ctrl.candidateName : 'the candidate')}}`. */
  candidateName?: string | null
  /**
   * `!!this.alert ? this.alert.id : null` — upstream sends `null`, not nothing, without an alert,
   * so an omitted prop is sent as `null` too.
   */
  alertId?: number | null
  /** On the standalone CandidateProfile state: `shortlist-export-enrichment-modal-slim`. */
  slim?: boolean
  /** `saveEnrichmentReport` — not awaited; see the header. */
  onSubmit: (payload: EnrichmentReportPayload) => Promise<void>
  /**
   * Upstream's `onReportSent`, which closes this dialogue AND schedules the "Thanks" one
   * `REPORT_ENRICHMENT_SENT_DIALOGUE_DELAY_MS` later. Both are the caller's: this component does
   * not close itself.
   */
  onSent: () => void
  /** The ✕, Escape, or a click outside the dialogue. */
  onClose: () => void
}

export function ReportEnrichmentModal({
  candidateId,
  candidateName,
  alertId = null,
  slim = false,
  onSubmit,
  onSent,
  onClose,
}: ReportEnrichmentModalProps): ReactElement {
  return (
    <UnifiedModal
      open
      windowClass={`dialog-unified shortlist-dialogue ${slim ? 'shortlist-export-enrichment-modal-slim' : 'shortlist-export-enrichment-modal'}`}
      onClose={onClose}
    >
      <div>
        <div className="extension-candidate-report-enrichment-x-icon">
          <i
            className="far fa-times fa-lg"
            role="button"
            aria-label="Close"
            tabIndex={0}
            onClick={onClose}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return
              event.preventDefault()
              onClose()
            }}
          />
        </div>
        <ReportEnrichmentForm
          candidateId={candidateId}
          candidateName={candidateName}
          alertId={alertId}
          onSubmit={onSubmit}
          onSent={onSent}
        />
      </div>
    </UnifiedModal>
  )
}

interface ReportEnrichmentFormProps {
  candidateId: number
  candidateName?: string | null
  alertId: number | null
  onSubmit: (payload: EnrichmentReportPayload) => Promise<void>
  onSent: () => void
}

/** `extension-alert-candidates-report-enrichment.tpl.html`, inside its component element. */
function ReportEnrichmentForm({
  candidateId,
  candidateName,
  alertId,
  onSubmit,
  onSent,
}: ReportEnrichmentFormProps): ReactElement {
  const [form, setForm] = useState(INITIAL_REPORT_ENRICHMENT_FORM)
  // The fields show what was typed; the form state holds the trimmed models (see reportEnrichment.ts).
  const [linkedinUrlView, setLinkedinUrlView] = useState('')
  const [notesView, setNotesView] = useState('')
  // `{{::…}}` is a one-time binding: the name is read once, at first render.
  const [displayName] = useState(() => (candidateName ? candidateName : 'the candidate'))

  const send = (): void => {
    if (!form.canSendReport) return
    // Unhandled upstream; the request layer's failure alert is the only report of a failure.
    onSubmit(buildEnrichmentReportPayload(form, candidateId, alertId)).catch(() => undefined)
    onSent()
  }

  const content = (
    <div className="extension-candidate-report-enrichment-container">
      <div className="extension-candidate-report-enrichment-header">
        <div className="extension-candidate-report-enrichment-cta">
          <span className="extension-candidate-report-enrichment-title">Report inadequate enrichment</span>
          <div className="mT2">
            Please describe the issue you encountered with {displayName}'s enrichment
          </div>
        </div>
      </div>
      <div className="extension-candidate-report-enrichment-reasons">
        {REPORT_ENRICHMENT_REASONS.map(({ reason, displayText }) => {
          const isLinkedin = reason === EnrichmentReportReason.IncorrectLinkedinProfile
          return (
            <div
              key={reason}
              className={`extension-candidate-report-enrichment-reason${isLinkedin ? ' linkedin-url-container' : ''}`}
            >
              <WooCheckbox
                checked={form.checked[reason] === true}
                onChange={(checked) => {
                  setForm((state) => toggleReportReason(state, reason, checked))
                }}
              >
                {displayText}
              </WooCheckbox>
              {isLinkedin ? (
                <span className="extension-candidate-report-enrichment-linkedin-url">
                  <input
                    type="text"
                    className="form-control input"
                    value={linkedinUrlView}
                    onChange={(event) => {
                      const value = event.target.value
                      setLinkedinUrlView(value)
                      setForm((state) => changeLinkedinUrl(state, value))
                    }}
                    placeholder="The correct LinkedIn profile (optional)"
                  />
                </span>
              ) : null}
            </div>
          )
        })}
      </div>
      <div className="extension-candidate-report-enrichment-notes">
        {/* Upstream also writes `type="text"` on the textarea; it is not an attribute a textarea
            has, nothing selects on it, and React's types refuse it. */}
        <textarea
          className="form-control nicer-scroller four-px-scroll"
          placeholder="Is there anything else you would like to add regarding this issue?"
          value={notesView}
          onChange={(event) => {
            const value = event.target.value
            setNotesView(value)
            setForm((state) => changeReportNotes(state, value))
          }}
          rows={8}
          maxLength={100000}
        />
      </div>
      <div
        className={`extension-candidate-report-enrichment-send pull-right${form.canSendReport ? '' : ' extension-candidate-report-enrichment-disabled'}`}
      >
        <button type="button" onClick={send}>
          Send
        </button>
      </div>
    </div>
  )
  // createElement, not JSX: a hyphenated tag in JSX needs an `IntrinsicElements` declaration.
  // The element is upstream's component host; no rule selects it today, but it is the DOM.
  return createElement('extension-alert-candidates-report-enrichment', null, content)
}

export interface ReportEnrichmentSentDialogueProps {
  /** On the standalone CandidateProfile state: ` modal-dialog-extended` (350px rather than 430px). */
  extended?: boolean
  /** The ✕, Escape, a click outside — or the 3000 ms auto-close. */
  onClose: () => void
}

/**
 * `showReportEnrichmentSentDialogue`. Closes itself after `REPORT_ENRICHMENT_SENT_AUTO_CLOSE_MS`;
 * closing it sooner cancels the timer, as upstream's `modal.closing` handler does.
 */
export function ReportEnrichmentSentDialogue({
  extended = false,
  onClose,
}: ReportEnrichmentSentDialogueProps): ReactElement {
  // The timer starts once, on open; a parent re-rendering with a new `onClose` must not restart it.
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])
  useEffect(() => {
    const timer = setTimeout(() => {
      onCloseRef.current()
    }, REPORT_ENRICHMENT_SENT_AUTO_CLOSE_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [])

  return (
    <UnifiedModal
      open
      windowClass={`dialog-unified shortlist-dialogue extension-candidates-modal-container${extended ? ' modal-dialog-extended' : ''}`}
      onClose={onClose}
    >
      <div>
        <span className="extension-candidates-modal-text">
          <span>
            <i className="fad fa-check-circle fa-lg extension-candidates-modal-positive" />
          </span>
          <span className="extension-candidates-modal-text-amount">
            <div>
              Thanks for reporting! <br />
              Your valuable input has been received and will be reviewed by our team
            </div>
          </span>
          <i
            className="far fa-times extension-candidates-modal-x cursor-pointer"
            role="button"
            aria-label="Close"
            tabIndex={0}
            onClick={onClose}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return
              event.preventDefault()
              onClose()
            }}
          />
        </span>
      </div>
    </UnifiedModal>
  )
}
